import React, { useEffect } from 'react'
import getSinglepost from '@pylot-data/api/operations/get-singlepost'
import { useRouter } from 'next/router'
import Sidebar from '@components/blog/Sidebar'
import BottomSection from '@components/blog/BottomSection'
import { useTranslation } from 'react-i18next'
import s from '@pagestyles/Blog.module.scss'
import { Layout } from '@components/common'
import Link from 'next/link'
import { Breadcrumbs } from '@components/alfakher/pylot-breadcrumbs'
import {
    authorName,
    authorTime,
    generateBlogIdentifier,
    splitBreadcrumb,
    authorTitleUrl
} from '@components/common/CMSBlockConst/cms-block-util'
import { RichContent } from '@corratech/pylot-rich-content'
import RelatedPost from '@components/blog/RelatedPost'
import BlogCommentForm from '@components/blog/CommentForm'
import getSeoDetails from '@pylot-data/api/operations/get-seo-details'
import { SEO, SEO_ENUMS } from '@components/seo'
import { GetServerSidePropsContext, InferGetStaticPropsType } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import SearchPost from '@components/blog/SearchPost'
import { getStaticProps } from './index'
import { NotFound } from '@components/404'
import { getStoreConfig } from '@config/hooks/useStoreConfig'

export const getServerSideProps = async ({
    locale,
    params
}: GetServerSidePropsContext) => {
    const urlPathParts = params!.id as string
    const singlePostData = await getSinglepost({
        postId: urlPathParts,
        locale: locale || ''
    })
    if (!singlePostData?.blogPost) {
        return {
            notFound: true
        }
    }
    const blogSeoDetails = await getSeoDetails({
        postId: urlPathParts,
        locale: locale || ''
    })
    return {
        props: {
            postDetails: singlePostData?.blogPost,
            seoData: blogSeoDetails?.blogPost,
            ...(await serverSideTranslations(locale!, ['common', 'blog']))
        }
    }
}

export default function PostDetails({
    postDetails,
    seoData
}: InferGetStaticPropsType<typeof getStaticProps>): JSX.Element {
    const { t } = useTranslation(['common', 'blog'])
    const router = useRouter()
    const postId: string = (router.query.id || '').toString()
    const posturllink = '/blog/'
    let title = ''
    let url = ''
    const config = getStoreConfig()
    useEffect(() => {
        title = document.title
        url = window.location.href
    }, [postId])

    useEffect(() => {
        setTimeout(() => {
            setAnyToShare()
        }, 2000)
    }, [])

    const setAnyToShare = () => {
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.src = 'https://static.addtoany.com/menu/page.js'
        document.head.appendChild(script)
    }

    if (!postDetails) {
        return <NotFound identifier={config.base.route?.cms_no_route!} />
    }

    return (
        <div className={s['blog-container']}>
            {seoData && (
                <SEO
                    blog={seoData}
                    page={SEO_ENUMS.BLOG}
                    blogBreadCrumbs={{ name: [postId] }}
                />
            )}
            <div className={s['container-classes']}>
                <div className={s['breadcrumb-inner']}>
                    <div className={s['breadcrumb-wrapper']}>
                        <Breadcrumbs
                            product={{
                                categories: [
                                    {
                                        name: t('Blog'),
                                        url_path: 'blog'
                                    }
                                ],
                                name: splitBreadcrumb(postDetails.title)
                            }}
                            separator=" | "
                            urlPrefix={t('')}
                        />
                    </div>
                </div>
                <div className={s['toggleSearchBar']}>
                    <SearchPost />
                </div>
                <div className={s['fullContent']}>
                    <div className={s['postDetailssection']}>
                        <div className={s['leftSection']}>
                            {postDetails != null ? (
                                <div>
                                    <div className={s['postImage']}>
                                        <img
                                            src={postDetails.featured_image}
                                            alt={postDetails.title}
                                            title={postDetails.title}
                                            width={823}
                                            height={560}
                                        />
                                    </div>
                                    <h1>{postDetails.title}</h1>
                                    <div className={s['detailsAuthor']}>
                                        {postDetails.author && (
                                            <>
                                                {t(`By`)}{' '}
                                                <Link
                                                    href={`${posturllink}author/${generateBlogIdentifier(
                                                        postDetails.author?.name
                                                    )}/${
                                                        postDetails?.author
                                                            ?.author_id
                                                    }`}
                                                >
                                                    <span>
                                                        {' '}
                                                        {authorName(
                                                            postDetails
                                                        )}
                                                    </span>
                                                </Link>
                                            </>
                                        )}
                                        {authorTime(postDetails)}
                                        <RichContent
                                            html={authorTitleUrl(postDetails)}
                                        />
                                        <div className={s['socialShare']}>
                                            <div className={s['spanTitle']}>
                                                {t(`Share`)}
                                            </div>
                                            <div
                                                className="a2a_kit a2a_kit_size_32 a2a_default_style"
                                                data-a2a-url={url}
                                                data-a2a-title={title}
                                            >
                                                <a
                                                    className="a2a_dd"
                                                    href="https://www.addtoany.com/share"
                                                    rel="noreferrer"
                                                >
                                                    <img
                                                        src="//static.addtoany.com/buttons/favicon.png"
                                                        width="20"
                                                        height="20"
                                                        alt=""
                                                    />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <span className={s['postDescription']}>
                                        <RichContent
                                            html={postDetails.filtered_content}
                                        />
                                    </span>
                                </div>
                            ) : (
                                ''
                            )}
                            <BlogCommentForm post_id={postDetails?.post_id} />
                            <RelatedPost postId={postId} />
                        </div>
                    </div>
                    <Sidebar />
                </div>
            </div>
            <BottomSection />
        </div>
    )
}

PostDetails.Layout = Layout
